import { IPetDetails } from "@/api_framework/api_modals/Pets";
import { ICustomerDetails } from "@/api_framework/api_modals/Users";
import ClipboardComponent from "@/app/components/ui/Clipboard";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import PaginationComponent from "@/app/components/ui/Pagination";
import Breed from "@/app/components/ui/Reusable/Breed";
import CustomerMembership from "@/app/components/ui/Reusable/CustomerMembership";
import PetAge from "@/app/components/ui/Reusable/PetAge";
import PetGender from "@/app/components/ui/Reusable/PetGender";
import PetName from "@/app/components/ui/Reusable/PetName";
import PetStatus from "@/app/components/ui/Reusable/PetStatus";
import PetWeight from "@/app/components/ui/Reusable/PetWeight";
import Species from "@/app/components/ui/Reusable/Species";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { setCustomerAndPetId } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { startCase } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { ISearchResultTableComponentProps } from "../../customer_profile_interface";
import { useAPIUtils } from "@/app/hooks/useAPIUtils";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import PetBirthdayIcon from "@/app/components/ui/Reusable/PetBirthdayIcon";

const ROW_HEIGHT = 72;
const TABLE_OVERHEAD = 260;

const getpageSize = () => {
  const availableHeight = window.innerHeight - TABLE_OVERHEAD;
  return Math.max(3, Math.floor(availableHeight / ROW_HEIGHT));
};

const SearchResultTableComponent: React.FC<ISearchResultTableComponentProps> = (props) => {
  const { maskMobile } = useUtils();
  const { NO_APPOINTMENTS } = useImageConstants();

  const [pageSize, setpageSize] = useState(getpageSize);
  //pagination
  const [currentPage, setCurrentPage] = useState(1);
  const { toastNotification } = useAPIUtils();

  useEffect(() => {
    const handleResize = () => {
      const nextPageSize = getpageSize();
      setpageSize((prevPageSize) => {
        if (prevPageSize !== nextPageSize) setCurrentPage(1);
        return nextPageSize;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return props?.data.slice(firstPageIndex, lastPageIndex);
  }, [props.data, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [props.data]);

  const dispatch = useAppDispatch();
  const { getRouteKey } = useRouter();
  const isCustomerSearchInProgress = useAppSelector((state) => state.loading.isCustomerSearchInProgress);

  const navigate = useNavigate();
  const handleCustomerSelection = (customerDetails: ICustomerDetails, petDetails: IPetDetails) => {
    localStorage.setItem("selectedPetId", petDetails?.pet_id);
    if (customerDetails) {
      try {
        dispatch(
          setCustomerAndPetId({
            customerId: customerDetails?.user_id,
            petId: petDetails?.pet_id,
          }),
        );

        toastNotification(
          `${customerDetails?.name}  ${petDetails?.name ? `and ${petDetails?.name}` : ""} has been selected`,
        );
        if (petDetails?.pet_id) {
          navigate(`${getRouteKey("APPOINTMENTS_TAB", "url")}/${customerDetails?.user_id}/${petDetails?.pet_id}`);
        } else {
          navigate(`${getRouteKey("APPOINTMENTS_TAB", "url")}/${customerDetails?.user_id}?new_user=true`);
        }
      } catch (e) {
        toast.error("Error while selecting");
      }
    }
  };

  return isCustomerSearchInProgress ? (
    <TableSkeletonComponent columnLength={6} bodyLength={5} />
  ) : props?.data?.length > 0 ? (
    <div className=" bg-white rounded-lg">
      <div className="scroll-inner mt-3 rounded-2xl max-h-[calc(100vh-200px)] overflow-y-auto">
        <table className="w-full min-w-[900px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
          <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal sticky top-0 z-10">
            <tr className="py-6">
              <th scope="col" className="p-2 py-3 whitespace-nowrap text-left text-xs font-medium text-black capitalize tracking-wider">
                SN
              </th>
              <th scope="col" className="p-2 py-3 whitespace-nowrap text-left text-xs font-medium text-black capitalize tracking-wider">
                Client Details
              </th>
              <th scope="col" className="p-2 py-3 whitespace-nowrap text-left text-xs font-medium text-black capitalize tracking-wider">
                Pet Details
              </th>
              <th scope="col" className="p-2 py-3 whitespace-nowrap text-left text-xs font-medium text-black capitalize tracking-wider">
                Membership
              </th>
              <th scope="col" className="p-2 py-3 whitespace-nowrap text-left text-xs font-medium text-black capitalize tracking-wider">
                Clinic
              </th>
              <th scope="col" className="p-2 py-3 whitespace-nowrap text-left text-xs font-medium text-black capitalize tracking-wider">
                Pet Status
              </th>
            </tr>
          </thead>
          <tbody className="text-xs">
            {currentTableData?.map((cd, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === currentTableData.length - 1;
              const isSingle = currentTableData.length === 1;

              return (
                <tr
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCustomerSelection(cd?.customer_details, cd?.pet_details);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCustomerSelection(cd?.customer_details, cd?.pet_details);
                    }
                  }}
                  tabIndex={0}
                  className={`
                    bg-white hover:bg-gray-50 cursor-pointer
                    ${isSingle ? "rounded-xl" : ""}
                    ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                    ${isLast && !isSingle ? "rounded-b-xl" : ""}
                  `}
                  key={`${cd.customer_details.user_id}:${idx}`}
                >
                  <td
                    className={`
                      p-2 whitespace-nowrap text-xs font-medium text-black
                      ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                      ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                      ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                    `}
                  >
                    {(currentPage - 1) * pageSize + idx + 1}
                  </td>
                  <td className="p-2 text-xs font-normal text-black whitespace-nowrap">
                    <span className="text-blue-500">{startCase(cd.customer_details.name)}</span>
                    <span className="flex justify-start items-center mt-1">
                      <span className="text-mini">{cd?.customer_details?.customer_code} </span>

                      <span onClick={(e) => e.stopPropagation()}>
                        <ClipboardComponent
                          className="ml-1"
                          tooltipText={`Copy ${cd?.customer_details?.customer_code}`}
                          text={`${cd?.customer_details?.customer_code}`}
                          copyId={`copy-user-code:${cd?.pet_details?.pet_id}`}
                        />
                      </span>
                    </span>

                  </td>
                  <td className="p-2 text-xs font-normal text-black whitespace-nowrap">
                    <div className="flex flex-col justify-start items-start">
                      <span className="flex">
                        <PetName
                          className="font-semibold"
                          petName={cd?.pet_details?.name}
                          petStatus={cd?.pet_details?.status}
                        />
                        <PetBirthdayIcon hasBirthday={cd?.pet_details?.has_birthday} petName={cd?.pet_details?.name} />
                      </span>
                      <span className="pt-1 text-gray-500 text-xs">
                        <Species className="0" speciesId={cd?.pet_details?.pet_species_type_id} />
                        <span>,</span>
                        <Breed
                          className="ml-1  italic text-xs"
                          breedId={cd?.pet_details?.pet_breed_id}
                          speciesId={cd?.pet_details?.pet_species_type_id}
                        />
                      </span>
                      <span className="pt-1 text-gray-500 text-xs">
                        <PetGender className="font-medium text-xs" gender={cd?.pet_details?.gender} />,{" "}
                        <PetWeight className="ml-1 font-medium text-xs" weight={cd?.pet_details?.weight} />
                        ,<PetAge className="ml-1 font-medium text-xs" dob={cd?.pet_details?.dob} />
                      </span>
                    </div>
                  </td>
                  <td className="p-2 text-xs font-normal text-black whitespace-nowrap">
                    <CustomerMembership membershipStatus={cd?.customer_details?.membership_status} />
                  </td>
                  <td className="p-2 whitespace-nowrap text-xs font-normal text-black">
                    {cd?.clinic_details ? (
                      <span className="text-xs">{cd?.clinic_details}</span>
                    ) : (
                      <span className="text-xs text-gray-400">NA</span>
                    )}
                  </td>
                  <td
                    className={`
                      p-2 whitespace-nowrap text-xs font-normal text-black
                      ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                      ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                      ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                    `}
                  >
                    <PetStatus
                      petStatus={cd?.pet_details?.status}
                      className="font-semibold text-[10px] md:text-xs 2xl:text-sm"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center pt-2">
        <PaginationComponent
          className="pagination-bar"
          currentPage={currentPage}
          totalCount={props?.data?.length}
          pageSize={pageSize}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </div>
  ) : (
    <div className="bg-white pt-4 pb-4 rounded-md w-full h-full 2xl:h-[600px] flex items-center justify-center ">
      {props?.hasSearched === false ? (
        <NoDataFoundComponent
          imageURL={NO_APPOINTMENTS}
          message="Search for a customer or pet"
          description="Enter a name, mobile number or customer ID to get started."
        />
      ) : (
        <NoDataFoundComponent
          imageURL={NO_APPOINTMENTS}
          message="No Results Found."
          description="No Customer Or Pets are found according to the query."
        />
      )}
    </div>
  );
};

export default SearchResultTableComponent;
